<template>
    <div>
        <!-- Draggable list with custom row structure -->
        <div class="table table-sm mb-0 hide-header">
            <Draggable v-model="localValue"
                      :disabled="editingRowIndex >= 0"
                      handle=".drag-handle"
                      @end="onDragEnd"
                      tag="div"
                      class="table-body">
                <div v-for="(item, index) in localValue" :key="index"
                     class="table-row d-flex align-items-center">
                    <!-- Handle column -->
                    <div class="table-cell drag-handle-column">
                        <div class="drag-handle" :class="{ 'text-muted': editingRowIndex >= 0 }" style="cursor: move;">
                            <i class="fas fa-grip-vertical"></i>
                        </div>
                    </div>

                    <!-- Name column -->
                    <div class="table-cell flex-fill">
                        <b-input v-model="editingRowData.name" v-if="editingRowIndex === index" autofocus maxlength="50" />
                        <span v-else>{{ item.name }}</span>
                    </div>

                    <!-- Actions column -->
                    <div class="table-cell actions">
                        <div class="d-flex" style="gap: 6px;">
                            <template v-if="editingRowIndex !== index">
                                <b-button variant="link" @click.prevent="startTableEdit(item, index)" title="Edit">
                                    <b-icon icon="pencil" variant="primary" />
                                </b-button>
                                <b-button variant="link" @click.prevent="removeTableItem(index)" title="Remove">
                                    <b-icon icon="x-circle-fill" variant="danger" />
                                </b-button>
                            </template>
                            <template v-else>
                                <b-button variant="link" title="Save" @click.prevent="saveTableEdit(index)">
                                    <b-icon icon="save" variant="primary" />
                                </b-button>
                                <b-button variant="link" title="Cancel Edit" @click.prevent="cancelTableEdit">
                                    <b-icon icon="x-lg" variant="" />
                                </b-button>
                            </template>
                        </div>
                    </div>
                </div>
            </Draggable>
        </div>

        <b-button size="sm" variant="primary" @click="addTableItem">
            Add Category
        </b-button>
    </div>
</template>

<script lang="ts">
import { Vue, Prop, Component, Watch } from "vue-property-decorator";
import Draggable from 'vuedraggable';
import { CaseCategory } from "@/models/Configuration";

@Component({
    name: "CaseCategoriesField",
    components: {
        Draggable
    }
})
export default class CaseCategoriesField extends Vue {
    @Prop({ default: () => [] }) value!: CaseCategory[];

    editingRowData: CaseCategory = { name: "", order: 0 };
    editingRowIndex: number = -1;
    localValue: CaseCategory[] = [];

    @Watch('value', { immediate: true, deep: true })
    onValueChange(newValue: CaseCategory[]) {
        this.localValue = [...newValue];
    }

    onDragEnd() {
        // Update order values based on new positions
        this.localValue.forEach((item, index) => {
            item.order = index;
        });
        this.$emit('input', [...this.localValue]);
    }

    startTableEdit(item: CaseCategory, index: number) {
        this.editingRowIndex = index;
        this.editingRowData = { ...item };
    }

    async addTableItem() {
        // If currently editing, commit the edit first
        if (this.editingRowIndex >= 0) {
            this.saveTableEdit(this.editingRowIndex);
            await this.$nextTick();
        }

        const cleanedArray = this.localValue.filter((a) => a.name.trim());
        const newOrder = cleanedArray.length > 0 ? Math.max(...cleanedArray.map(c => c.order)) + 1 : 0;
        cleanedArray.push({ name: "", order: newOrder });
        this.localValue = cleanedArray;
        this.$emit('input', [...this.localValue]);
        this.startTableEdit({ name: "", order: newOrder }, cleanedArray.length - 1);
    }

    removeTableItem(index: number) {
        const array = [...this.localValue];
        array.splice(index, 1);
        // Update order values after removal
        array.forEach((item, idx) => {
            item.order = idx;
        });
        this.localValue = array;
        this.$emit('input', [...this.localValue]);
    }

    cancelTableEdit() {
        const editingIndex = this.editingRowIndex;
        const editingValue = this.localValue[editingIndex].name;
        if (!editingValue) {
            this.removeTableItem(editingIndex);
        }
        this.editingRowIndex = -1;
    }

    saveTableEdit(index: number) {
        const array = [...this.localValue];
        if (!this.editingRowData.name.trim()) {
            array.splice(index, 1);
            // Update order values after removal
            array.forEach((item, idx) => {
                item.order = idx;
            });
        } else {
            array.splice(index, 1, this.editingRowData);
        }
        this.localValue = array;
        this.$emit('input', [...this.localValue]);
        this.editingRowIndex = -1;
    }
}
</script>

<style scoped lang="scss">
::v-deep(thead th),
::v-deep(td) {
    padding: 1px 7px !important;
}

::v-deep(td) {
    height: 38px !important;
}

table ::v-deep(.actions) {
    width: 90px;
}

table ::v-deep(.drag-handle-column) {
    width: 30px;
    text-align: center;
}

.drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;

    &.text-muted {
        cursor: not-allowed !important;
        opacity: 0.5;
    }
}

// Custom table styles for draggable mode
.table {
    &.hide-header .table-body .table-row:first-child {
        // Hide header row effect in custom structure
    }
}

.table-body {
    display: block;
}

.table-row {
    border: 1px solid #dee2e6;
    border-top: 0;
    padding: 1px 7px;
    min-height: 38px;

    &:first-child {
        border-top: 1px solid #dee2e6;
    }

    &:hover {
        background-color: rgba(0, 0, 0, 0.075);
    }
}

.table-cell {
    display: flex;
    align-items: center;

    &.drag-handle-column {
        width: 30px;
        flex-shrink: 0;
        text-align: center;
        padding: 0;
    }

    &.actions {
        width: 100px;
        flex-shrink: 0;
        justify-content: flex-end;
        padding-left: 0.5rem;
    }

    &.flex-fill {
        flex: 1;
        padding-right: 0.5rem;
    }
}
</style>
